import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import {
  DWH_EDIT,
  ENV_SCHEDULE,
  TEAMS_BOT_CONNECT,
  TEAMS_BOT_TOKEN,
} from '@elementary/common-ui/constants/urlParams';
import { Accordion } from '@elementary/common-ui/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DESTINATION_OPTIONS,
  INTEGRATION_OPTIONS,
  INTEGRATION_RULE_COUNTS,
  NANGO,
  TEAMS_BOT,
} from '@elementary/common-ui/constants/queryKeys';
import { AccountEnvironment } from '@elementary/common-ui/types';
import { AlertIntegrationType } from '@elementary/common-ui/services/gql/types';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import useIsSyncEnabled from 'shared/hooks/useIsSyncEnabled';
import useSnackbar from 'shared/hooks/useSnackbar';
import useNango from 'shared/hooks/useNango';
import * as settingsApi from 'shared/services/settingsApi';
import * as api from 'shared/services/api';
import {
  EnvironmentIntegrations,
  IntegrationType,
  VersionControl,
} from 'shared/types';
import { parseEnvironmentError, parseIntegrationError } from '../../../utils';
import EnvironmentDrawer from '../EnvironmentDrawer/EnvironmentDrawer';
import EnvScheduleDrawer from '../EnvScheduleDrawer/EnvScheduleDrawer';
import AtlanDialog from '../IntegrationDialogs/Atlan';
import BiToolDialog from '../IntegrationDialogs/BiTool/BiToolDialog';
import DeleteBiToolDialog from '../IntegrationDialogs/BiTool/DeleteBiToolDialog';
import DeleteEnvironmentDialog from '../IntegrationDialogs/DeleteEnvironmentDialog';
import DisconnectIntegrationDialog from '../IntegrationDialogs/DisconnectIntegrationDialog';
import IncidentManagementToolDialog from '../IntegrationDialogs/IncidentManagementTool/IncidentManagementToolDialog';
import MessagingDialog from '../IntegrationDialogs/MessagingDialog';
import { TeamsBotDialogStep } from '../IntegrationDialogs/TeamsBotDialog';
import RepositoryDialog from '../IntegrationDialogs/Repository/RepositoryDialog';
import TicketingToolDialog from '../IntegrationDialogs/TicketingTool/TicketingToolDialog';
import DeleteTicketingToolDialog from '../IntegrationDialogs/TicketingTool/DeleteTicketingToolDialog';
import JiraWebhookDialog from '../IntegrationDialogs/TicketingTool/JiraWebhookDialog';
import JiraReauthDialog from '../IntegrationDialogs/TicketingTool/JiraReauthDialog';
import LinearReauthDialog from '../IntegrationDialogs/TicketingTool/LinearReauthDialog';
import DataCatalogDialog from '../IntegrationDialogs/dataCatalog/DataCatalogDialog';
import DeleteDataCatalogDialog from '../IntegrationDialogs/dataCatalog/DeleteDataCatalogDialog';
import FivetranDialog from '../IntegrationDialogs/EtlTool/FivetranDialog';
import useEnvIntegrations from './useEnvIntegrations';
import biMultipleDestinationsRenderer from './biMultipleDestinationsRenderer';
import IntegrationAccordionDetails from './IntegrationAccordionDetails';
import IntegrationAccordionSummary from './IntegrationAccordionSummary';
import { DialogTypes } from './types';
import TicketingMultipleDestinations from './TicketingMultipleDestinations';
import { WAREHOUSE_META_STORES_MAP } from './constants';
import multipleDataStoresRenderer from './multipleDataCatalogRenderer';
import MessagingMultipleDestinations from './MessagingMultipleDestinations';

type Props = {
  environmentData: AccountEnvironment;
  isFocused: boolean;
  envIntegrations?: EnvironmentIntegrations;
  isIntegrationsLoading?: boolean;
  shouldHandleTeamsBotConnect?: boolean;
};

const IntegrationAccordion = React.forwardRef(
  (
    {
      environmentData,
      isFocused,
      envIntegrations,
      isIntegrationsLoading,
      shouldHandleTeamsBotConnect = false,
    }: Props,
    ref: any,
  ) => {
    const [dialog, setDialog] = useState<DialogTypes>(null);
    const [teamsBotConnectError, setTeamsBotConnectError] = useState<
      string | null
    >(null);
    const [isTeamsBotLinking, setIsTeamsBotLinking] = useState(false);
    const [teamsBotInitialStep, setTeamsBotInitialStep] = useState<
      TeamsBotDialogStep | undefined
    >();
    const handledTeamsBotConnectToken = useRef<string | null>(null);
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const hasPermission = useAccountPermission();

    const isDwhDrawerOpen = searchParams.get(DWH_EDIT) === environmentData.id;
    const setIsDwhDrawerOpen = (open: boolean) => {
      if (open) {
        setSearchParams((prev) => {
          prev.set(DWH_EDIT, environmentData.id);
          return prev;
        });
      } else {
        setSearchParams((prev) => {
          prev.delete(DWH_EDIT);
          return prev;
        });
      }
    };

    const isEnvScheduleDrawerOpen =
      searchParams.get(ENV_SCHEDULE) === environmentData.id;
    const setIsEnvScheduleDrawerOpen = (open: boolean) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (open) {
        newSearchParams.set(ENV_SCHEDULE, environmentData.id);
      } else {
        newSearchParams.delete(ENV_SCHEDULE);
      }
      setSearchParams(newSearchParams);
    };

    const [isSyncEnabled, setIsSyncEnabled] = useState(
      useIsSyncEnabled(environmentData),
    );
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [selectedBiIntegrationId, setSelectedBiIntegrationId] = useState<
      string | null
    >(null);
    const [selectedTicketingIntegrationId, setSelectedTicketingIntegrationId] =
      useState<string | null>(null);
    const [selectedMessagingType, setSelectedMessagingType] =
      useState<AlertIntegrationType | null>(null);
    const [dataCatalogIntegrationId, setDataCatalogIntegrationId] = useState<
      string | null
    >(null);
    const [pendingJiraWebhookName, setPendingJiraWebhookName] = useState<
      string | null
    >(null);
    const [jiraWebhookIsInitialSetup, setJiraWebhookIsInitialSetup] =
      useState(false);
    const lastSubmittedTicketingForm = useRef<{
      type: string;
      id?: string;
      name?: string;
    } | null>(null);
    const [disconnectIntegrationType, setDisconnectIntegrationType] =
      useState<IntegrationType | null>(null);

    const { openSnackbar } = useSnackbar();
    const { success: nangoSuccess } = useNango();

    const clearTeamsBotConnectParams = () => {
      setSearchParams((prev) => {
        prev.delete(TEAMS_BOT_CONNECT);
        prev.delete(TEAMS_BOT_TOKEN);
        return prev;
      });
    };

    useEffect(() => {
      if (
        !shouldHandleTeamsBotConnect ||
        searchParams.get(TEAMS_BOT_CONNECT) !== '1'
      ) {
        return;
      }

      const token = searchParams.get(TEAMS_BOT_TOKEN) ?? '';
      if (handledTeamsBotConnectToken.current === token) {
        return;
      }
      handledTeamsBotConnectToken.current = token;

      clearTeamsBotConnectParams();
      setIsExpanded(true);
      setSelectedMessagingType('MICROSOFT_TEAMS_BOT');
      setDialog('notification');
      setTeamsBotInitialStep('connecting');

      if (!token) {
        setTeamsBotConnectError('Missing connect token in URL.');
        setIsTeamsBotLinking(false);
        return;
      }

      setTeamsBotConnectError(null);
      setIsTeamsBotLinking(true);

      const linkFromTeams = async () => {
        try {
          const res = await settingsApi.linkTeamsBotInstallation(token);
          await queryClient.invalidateQueries([TEAMS_BOT]);
          if (res.success) {
            await queryClient.invalidateQueries([DESTINATION_OPTIONS]);
            await queryClient.invalidateQueries([INTEGRATION_OPTIONS]);
          }
          if (!res.success) {
            setTeamsBotConnectError(res.message);
          }
        } catch {
          setTeamsBotConnectError(
            'Failed to connect. Please retry from the Teams welcome card, or type @Elementary connect in your Teams channel to get a fresh link.',
          );
        } finally {
          setIsTeamsBotLinking(false);
        }
      };

      linkFromTeams();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldHandleTeamsBotConnect, searchParams, queryClient]);

    const {
      slackQuery,
      slackMutation,
      slackIntegrationMutation,
      isSlackConnected,
      isTeamsConnected,
      isTeamsLoading,
      isTeamsBotConnected,
      isTeamsBotLoading,
      teamsBotManifestUrl,
      teamsBotAppId,
      teamsBotConnectedWorkspaces,
      isTicketingConnected,
      pagerDutyQuery,
      pagerDutyIntegrationMutation,
      opsgenieQuery,
      opsgenieIntegrationMutation,
      incidentIoQuery,
      incidentIoIntegrationMutation,
      rootlyQuery,
      rootlyIntegrationMutation,
      biIntegrationsQuery,
      biToolMutation,
      deleteBiIntegrationMutation,
      syncNowBiIntegrationMutation,
      codeProjectsMutation,
      codeProjectType,
      ticketingQuery,
      ticketingMutation,
      deleteTicketingMutation,
      servicenowConfigQuery,
      isRepositoryConnected,
      isRepositoryLoading,
      environmentMutation,
      envScheduleMutation,
      deleteEnvironmentMutation,
      isBiToolConnected,
      isIncidentManagementConnected,
      dataCatalogMutation,
      deleteCatalogMutation,
      dataCatalogConfigQuery,
      connectedMessagingTypes,
      microsoftTeamsDefaultsQuery,
      atlanQuery,
      atlanTestMutation,
      atlanSaveMutation,
      atlanDeleteMutation,
    } = useEnvIntegrations({
      onUpdateSuccess: () => {
        const submitted = lastSubmittedTicketingForm.current;
        if (submitted?.type === 'jira') {
          if (submitted.id) {
            setJiraWebhookIsInitialSetup(false);
            setDialog('jiraWebhook');
          } else {
            setJiraWebhookIsInitialSetup(true);
            setPendingJiraWebhookName(submitted.name ?? '');
            setDialog(null);
            setSelectedTicketingIntegrationId(null);
          }
        } else {
          setDialog(null);
          setSelectedTicketingIntegrationId(null);
        }
        lastSubmittedTicketingForm.current = null;
        setIsDwhDrawerOpen(false);
        setIsEnvScheduleDrawerOpen(false);
        setIsSyncEnabled(false);
      },
      envId: environmentData.id,
    });

    const supportedMetaStores =
      WAREHOUSE_META_STORES_MAP[environmentData.dwhConnection.warehouseType];
    const canDeleteEnvironment = hasPermission('DELETE_ENVIRONMENTS');

    const effectiveFivetranIntegration = envIntegrations?.etl_tools.find(
      (t) => t.platform_type === 'fivetran',
    );

    const selectedBiIntegration = biIntegrationsQuery.data?.find(
      (biTool) => biTool.id === selectedBiIntegrationId,
    );

    const selectedTicketingIntegration =
      ticketingQuery.data?.integrations?.find(
        (ticketing) => ticketing.id === selectedTicketingIntegrationId,
      );

    const selectedDataCatalogIntegration = dataCatalogConfigQuery.data?.find(
      (dataCatalog) => dataCatalog.id === dataCatalogIntegrationId,
    );

    const alertIntegrationsQuery = useQuery([INTEGRATION_OPTIONS], () =>
      api.fetchAlertIntegrations(),
    );

    const ruleCountsQuery = useQuery(
      [INTEGRATION_RULE_COUNTS, environmentData.id],
      () => settingsApi.fetchIntegrationRuleCounts(environmentData.id),
    );

    const messagingIntegrations = connectedMessagingTypes.map((type) => {
      const integration = alertIntegrationsQuery.data?.find(
        (i) => i.type.toLowerCase() === type.toLowerCase(),
      );
      const ruleCount = integration?.id
        ? ruleCountsQuery.data?.[integration.id] || 0
        : 0;
      const usageText =
        ruleCount > 0
          ? `Used by ${pluralizeString(ruleCount, 'alert rule', 'alert rules')}`
          : undefined;

      if (type === 'SLACK') {
        return {
          type,
          name: slackQuery.data?.teamName || 'Slack',
          usageText,
          issues: integration?.issues ?? [],
        };
      }
      if (type === 'MICROSOFT_TEAMS') {
        return {
          type,
          name:
            microsoftTeamsDefaultsQuery.data?.defaultDestination
              ?.defaultWebhookName || 'Microsoft Teams',
          usageText,
          issues: integration?.issues ?? [],
        };
      }
      if (type === 'MICROSOFT_TEAMS_BOT') {
        return {
          type,
          name: 'Microsoft Teams App',
          usageText,
          issues: integration?.issues ?? [],
        };
      }
      return { type, name: type, usageText, issues: integration?.issues ?? [] };
    });

    const installedNangoAppsQuery = useQuery(
      [NANGO],
      () => settingsApi.fetchNangoInstalledApps(),
      {},
    );

    const messagingRuleCount =
      alertIntegrationsQuery.data
        ?.filter(
          (i) =>
            i.type === 'slack' ||
            i.type === 'microsoft_teams' ||
            i.type === 'microsoft_teams_bot',
        )
        .reduce((total, i) => total + (ruleCountsQuery.data?.[i.id] || 0), 0) ||
      undefined;
    const incidentIntegration = alertIntegrationsQuery.data?.find(
      (i) =>
        i.type === 'pager_duty' ||
        i.type === 'opsgenie' ||
        i.type === 'incident_io' ||
        i.type === 'rootly',
    );
    const incidentManagementRuleCount = incidentIntegration
      ? ruleCountsQuery.data?.[incidentIntegration.id]
      : undefined;

    const handleDisconnect = useCallback(
      (integrationType: IntegrationType) => {
        const found = alertIntegrationsQuery.data?.find(
          (i) => i.type.toLowerCase() === integrationType.toLowerCase(),
        );
        if (!found) {
          openSnackbar({
            message: 'Failed to load integration details. Please try again.',
            variant: 'error',
          });
          return;
        }
        setDisconnectIntegrationType(integrationType);
        setDialog('disconnectIntegration');
      },
      [alertIntegrationsQuery.data, openSnackbar],
    );

    const disconnectIntegration = alertIntegrationsQuery.data?.find(
      (i) => i.type.toLowerCase() === disconnectIntegrationType?.toLowerCase(),
    );

    const environmentError = useMemo(
      () => parseEnvironmentError(environmentMutation.error),
      [environmentMutation.error],
    );
    const repositoryError = codeProjectsMutation.error;

    const ticketingError = ticketingMutation.error;

    const { mutate: syncNowMutate } = useMutation(
      () => settingsApi.syncNow(environmentData.id),
      {
        onSuccess: () => {
          openSnackbar({
            message: 'Sync triggered successfully!',
            variant: 'success',
          });
          setIsSyncEnabled(false);
        },
        onError: () => {
          openSnackbar({
            message: 'Failed to trigger sync',
            variant: 'error',
          });
        },
      },
    );

    useEffect(() => {
      if (!dialog) {
        environmentMutation.reset?.();
        slackMutation.reset?.();
        biToolMutation.reset?.();
        deleteBiIntegrationMutation.reset?.();
        codeProjectsMutation.reset?.();
        ticketingMutation.reset?.();
        deleteTicketingMutation.reset?.();
        deleteEnvironmentMutation.reset?.();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dialog]);

    useEffect(() => {
      if (!isDwhDrawerOpen) {
        environmentMutation.reset?.();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDwhDrawerOpen]);

    useEffect(() => {
      if (
        handledTeamsBotConnectToken.current !== null ||
        (shouldHandleTeamsBotConnect &&
          searchParams.get(TEAMS_BOT_CONNECT) === '1')
      ) {
        return;
      }
      setIsExpanded(isFocused);
    }, [isFocused, shouldHandleTeamsBotConnect, searchParams]);

    useEffect(() => {
      if (pendingJiraWebhookName && ticketingQuery.data?.integrations) {
        const found = ticketingQuery.data.integrations.find(
          (i) => i.type === 'jira' && i.name === pendingJiraWebhookName,
        );
        if (found) {
          setSelectedTicketingIntegrationId(found.id);
          setDialog('jiraWebhook');
          setPendingJiraWebhookName(null);
        }
      }
    }, [pendingJiraWebhookName, ticketingQuery.data]);

    useEffect(() => {
      if (nangoSuccess && selectedTicketingIntegrationId) {
        ticketingQuery.refetch();
        openSnackbar({
          message:
            'Successfully re-authenticated Jira integration with new permissions',
          variant: 'success',
        });
        setSelectedTicketingIntegrationId(null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nangoSuccess]);

    return (
      <div ref={ref}>
        <Accordion
          expanded={isExpanded}
          onChange={(_, val) => setIsExpanded(val)}
        >
          <IntegrationAccordionSummary
            onDwhDrawerOpen={() => setIsDwhDrawerOpen(true)}
            onEnvScheduleDrawerOpen={() => setIsEnvScheduleDrawerOpen(true)}
            onSyncNow={syncNowMutate}
            isSyncEnabled={isSyncEnabled}
            envData={environmentData}
          />

          <IntegrationAccordionDetails
            onDialogChange={setDialog}
            isSlackConnected={isSlackConnected}
            isTeamsConnected={isTeamsConnected}
            isTeamsBotConnected={isTeamsBotConnected}
            isIncidentManagementConnected={isIncidentManagementConnected}
            isBiToolConnected={isBiToolConnected}
            isRepositoryConnected={isRepositoryConnected}
            isTicketingConnected={isTicketingConnected}
            isTicketingLoading={ticketingQuery.isLoading}
            isSlackLoading={slackQuery.isLoading}
            isTeamsLoading={isTeamsLoading}
            isIncidentManagementLoading={pagerDutyQuery.isLoading}
            isBiToolLoading={biIntegrationsQuery.isLoading}
            isRepositoryLoading={isRepositoryLoading}
            hasDbtProjects={environmentData.hasDbtProjects}
            supportedMetaStores={supportedMetaStores}
            dataCatalogConfig={dataCatalogConfigQuery.data}
            isDataCatalogLoading={dataCatalogConfigQuery.isLoading}
            biIntegrations={biIntegrationsQuery.data}
            biMultipleDestinationsRenderer={() =>
              biMultipleDestinationsRenderer({
                biIntegrations: biIntegrationsQuery.data || [],
                onChange: (method, id) => {
                  if (method === 'sync') {
                    syncNowBiIntegrationMutation.mutate(id);
                  } else {
                    setSelectedBiIntegrationId(id);
                    setDialog(`${method}BiTool` as DialogTypes);
                  }
                },
              })
            }
            ticketingIntegrations={ticketingQuery.data?.integrations}
            ticketingMultipleDestinationsRenderer={() => (
              <TicketingMultipleDestinations
                ticketingIntegrations={ticketingQuery.data?.integrations || []}
                setSelectedTicketingIntegrationId={
                  setSelectedTicketingIntegrationId
                }
                setDialog={setDialog}
              />
            )}
            messagingIntegrations={messagingIntegrations}
            messagingMultipleDestinationsRenderer={() => (
              <MessagingMultipleDestinations
                messagingIntegrations={messagingIntegrations}
                setSelectedMessagingType={setSelectedMessagingType}
                setDialog={setDialog}
                onDisconnect={handleDisconnect}
              />
            )}
            dataCatalogMultipleDestinationsRenderer={() =>
              multipleDataStoresRenderer({
                integrations: dataCatalogConfigQuery.data,
                onChange: (option: string, id: string) => {
                  setDataCatalogIntegrationId(id);
                  setDialog(
                    option === 'delete' ? 'deleteCatalog' : 'dataCatalog',
                  );
                },
              })
            }
            etlToolIntegrations={{
              fivetran: effectiveFivetranIntegration,
            }}
            isEtlToolLoading={isIntegrationsLoading}
            messagingRuleCount={messagingRuleCount}
            incidentManagementRuleCount={incidentManagementRuleCount}
            isAtlanConnected={atlanQuery.data?.connected}
            isAtlanLoading={atlanQuery.isLoading}
          />
        </Accordion>

        <EnvironmentDrawer
          open={isDwhDrawerOpen}
          onClose={() => setIsDwhDrawerOpen(false)}
          environmentId={environmentData.id}
          onSubmit={environmentMutation.mutate}
          loading={environmentMutation.isLoading}
          error={environmentError}
          onDeleteClick={
            canDeleteEnvironment
              ? () => setDialog('deleteEnvironment')
              : undefined
          }
        />

        <EnvScheduleDrawer
          open={isEnvScheduleDrawerOpen}
          onSubmit={envScheduleMutation.mutate}
          onClose={() => setIsEnvScheduleDrawerOpen(false)}
          loading={envScheduleMutation.isLoading}
          data={environmentData}
          error={
            envScheduleMutation.error
              ? ((envScheduleMutation.error as any)?.response?.data
                  ?.error_message as string) || 'Error'
              : undefined
          }
        />

        <MessagingDialog
          open={dialog === 'notification'}
          onClose={() => {
            setDialog(null);
            setSelectedMessagingType(null);
            setTeamsBotInitialStep(undefined);
            setTeamsBotConnectError(null);
          }}
          slackData={slackQuery.data}
          onSlackSubmit={slackMutation.mutate}
          onSlackIntegrationSubmit={slackIntegrationMutation.mutate}
          slackIntegrationResponse={slackIntegrationMutation.data}
          slackIntegrationError={parseIntegrationError(
            slackIntegrationMutation.error,
          )}
          isTeamsBotConnected={isTeamsBotConnected}
          isTeamsBotLoading={isTeamsBotLoading}
          teamsBotManifestUrl={teamsBotManifestUrl}
          teamsBotAppId={teamsBotAppId}
          teamsBotConnectedWorkspaces={teamsBotConnectedWorkspaces}
          forceTeamsBot={Boolean(teamsBotInitialStep)}
          teamsBotInitialStep={teamsBotInitialStep}
          teamsBotConnectError={teamsBotConnectError}
          isTeamsBotLinking={isTeamsBotLinking}
          error={parseIntegrationError(slackMutation.error)}
          loading={slackMutation.isLoading}
          envId={environmentData.id}
          slackInstalled={installedNangoAppsQuery.data?.slack}
          edit={!!selectedMessagingType}
          editingType={selectedMessagingType}
          connectedTypes={connectedMessagingTypes}
          onDisconnect={handleDisconnect}
        />

        <IncidentManagementToolDialog
          open={dialog === 'incidentManagement'}
          onClose={() => setDialog(null)}
          data={{
            pagerduty: pagerDutyQuery.data,
            opsgenie: opsgenieQuery.data,
            incidentIo: incidentIoQuery.data,
            rootly: rootlyQuery.data,
          }}
          onPagerDutyIntegrationSubmit={pagerDutyIntegrationMutation.mutate}
          isPagerDutyIntegrationLoading={pagerDutyIntegrationMutation.isLoading}
          pagerDutySetIntegrationError={parseIntegrationError(
            pagerDutyIntegrationMutation.error,
          )}
          onOpsgenieIntegrationSubmit={(data) =>
            opsgenieIntegrationMutation.mutate(data)
          }
          opsgenieSetIntegrationError={parseIntegrationError(
            opsgenieIntegrationMutation.error,
          )}
          opsgenieLoading={opsgenieIntegrationMutation.isLoading}
          onIncidentIoIntegrationSubmit={(data) =>
            incidentIoIntegrationMutation.mutate(data)
          }
          incidentIoSetIntegrationError={parseIntegrationError(
            incidentIoIntegrationMutation.error,
          )}
          incidentIoLoading={incidentIoIntegrationMutation.isLoading}
          onRootlyIntegrationSubmit={(data) =>
            rootlyIntegrationMutation.mutate(data)
          }
          rootlySetIntegrationError={parseIntegrationError(
            rootlyIntegrationMutation.error,
          )}
          rootlyLoading={rootlyIntegrationMutation.isLoading}
          error={parseIntegrationError(pagerDutyQuery.error)}
          onDisconnect={handleDisconnect}
        />

        <BiToolDialog
          open={dialog === 'updateBiTool' || dialog === 'createBiTool'}
          mode={
            dialog === 'updateBiTool'
              ? 'update'
              : dialog === 'createBiTool'
              ? 'create'
              : undefined
          }
          onClose={() => {
            setDialog(null);
            setSelectedBiIntegrationId(null);
          }}
          onSubmit={(data) => {
            const mode = dialog === 'updateBiTool' ? 'update' : 'create';
            biToolMutation.mutate({ data, mode });
            setSelectedBiIntegrationId(null);
          }}
          error={parseIntegrationError(biToolMutation.error)}
          loading={biToolMutation.isLoading}
          selectedIntegration={selectedBiIntegration}
        />

        <DeleteBiToolDialog
          open={dialog === 'deleteBiTool'}
          onClose={() => {
            setDialog(null);
            setSelectedBiIntegrationId(null);
          }}
          onSubmit={(id) => {
            deleteBiIntegrationMutation.mutate(id);
          }}
          selectedIntegration={selectedBiIntegration}
          loading={deleteBiIntegrationMutation.isLoading}
        />

        <RepositoryDialog
          open={dialog === 'repository'}
          onClose={() => setDialog(null)}
          envId={environmentData.id}
          appInstalled={
            codeProjectType ? (codeProjectType as VersionControl) : null
          }
          error={parseIntegrationError(repositoryError)}
        />

        <TicketingToolDialog
          open={dialog === 'ticketing' || dialog === 'updateTicketingTool'}
          onClose={() => {
            setDialog(null);
            setSelectedTicketingIntegrationId(null);
          }}
          onSubmit={(data) => {
            lastSubmittedTicketingForm.current = {
              type: data.type,
              id: data.id,
              name: data.name,
            };
            ticketingMutation.mutate(data);
          }}
          error={parseIntegrationError(ticketingError)}
          envId={environmentData.id}
          loading={ticketingMutation.isLoading}
          data={selectedTicketingIntegration}
          servicenowConfig={servicenowConfigQuery.data}
        />

        <DeleteTicketingToolDialog
          open={dialog === 'deleteTicketingTool'}
          onClose={() => {
            setDialog(null);
            setSelectedTicketingIntegrationId(null);
          }}
          onSubmit={(id) => {
            deleteTicketingMutation.mutate(id);
          }}
          selectedIntegration={selectedTicketingIntegration}
          loading={deleteTicketingMutation.isLoading}
        />

        {dialog === 'jiraWebhook' && selectedTicketingIntegration && (
          <JiraWebhookDialog
            open
            onClose={() => {
              setDialog(null);
              setSelectedTicketingIntegrationId(null);
              setJiraWebhookIsInitialSetup(false);
            }}
            envId={environmentData.id}
            integrationId={selectedTicketingIntegration.id}
            isInitialSetup={jiraWebhookIsInitialSetup}
          />
        )}

        <JiraReauthDialog
          open={dialog === 'jiraReauth'}
          onClose={() => {
            setDialog(null);
            setSelectedTicketingIntegrationId(null);
          }}
          environmentId={environmentData.id}
          integrationId={selectedTicketingIntegrationId ?? ''}
        />

        <LinearReauthDialog
          open={dialog === 'linearReauth'}
          onClose={() => {
            setDialog(null);
            setSelectedTicketingIntegrationId(null);
          }}
          environmentId={environmentData.id}
        />

        <DataCatalogDialog
          open={dialog === 'dataCatalog'}
          onClose={() => {
            setDialog(null);
            dataCatalogMutation.reset?.();
            setDataCatalogIntegrationId(null);
          }}
          onSubmit={(data) => dataCatalogMutation.mutate(data)}
          supportedMetaStores={supportedMetaStores}
          error={parseIntegrationError(dataCatalogMutation.error)}
          isLoading={dataCatalogMutation.isLoading}
          data={selectedDataCatalogIntegration}
        />

        <DeleteDataCatalogDialog
          open={dialog === 'deleteCatalog'}
          onClose={() => {
            setDialog(null);
            setDataCatalogIntegrationId(null);
          }}
          onSubmit={(id) => deleteCatalogMutation.mutate({ id })}
          loading={deleteCatalogMutation.isLoading}
          selectedIntegration={selectedDataCatalogIntegration}
        />

        <FivetranDialog
          open={dialog === 'fivetran'}
          onClose={() => setDialog(null)}
          envId={environmentData.id}
          fivetranIntegrationId={effectiveFivetranIntegration?.id}
        />

        <AtlanDialog
          open={dialog === 'atlan'}
          onClose={() => setDialog(null)}
          data={atlanQuery.data}
          isLoading={atlanQuery.isLoading}
          testMutation={atlanTestMutation}
          saveMutation={atlanSaveMutation}
          deleteMutation={atlanDeleteMutation}
        />

        <DisconnectIntegrationDialog
          open={dialog === 'disconnectIntegration'}
          onClose={() => {
            setDialog(null);
            setDisconnectIntegrationType(null);
          }}
          integrationId={disconnectIntegration?.id ?? null}
          integrationName={disconnectIntegration?.name ?? ''}
          integrationTypeKey={
            (disconnectIntegration?.type as IntegrationType) ?? 'slack'
          }
        />

        <DeleteEnvironmentDialog
          open={dialog === 'deleteEnvironment'}
          onClose={() => setDialog(null)}
          onConfirm={() => deleteEnvironmentMutation.mutate()}
          environment={environmentData}
          canDelete={canDeleteEnvironment}
          loading={deleteEnvironmentMutation.isLoading}
        />
      </div>
    );
  },
);

export default IntegrationAccordion;
